import { Icon } from '@delmonicos/shared-design-system-react/src/components/icon/icon';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { Tag } from '@delmonicos/shared-design-system-react/src/components/tag/tag';
import { Tooltip } from '@delmonicos/shared-design-system-react/src/components/tooltip/tooltip';
import { computeTreePhaseVoltage } from '@delmonicos/shared-sdk/libs/core/src/helpers/compute-phase-voltage';
import { GatewayChannelWithChargePointInfoModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/charge-points/models/outputs/gateway-channel-with-charge-point-info.model';
import { PowerType } from '@delmonicos/shared-sdk/libs/cpms/src/libs/ocpi/src/enums/ocpi-protocol-enums';
import { CellContext } from '@tanstack/react-table';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { mapStatusToGenericConnectorStatus } from '@app/feature/charge-point/helper/connector-status.helper';
import { mapTypeToGenericConnectorType } from '@app/feature/charge-point/helper/connector-type.helper';

import * as css from './channel-list.module.scss';

export interface ChannelListPhysicalSocketsCellProps
  extends CellContext<
    GatewayChannelWithChargePointInfoModel,
    GatewayChannelWithChargePointInfoModel['physicalSockets']
  > {}

export const ChannelListPhysicalSocketsCell = (
  props: ChannelListPhysicalSocketsCellProps,
): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { getValue } = props;
  const { t } = useTranslation();

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <Stack direction="row" gap={GapEnum.XS}>
      {getValue().map((socket) => {
        const status = mapStatusToGenericConnectorStatus(socket.ocppStatus);
        if (!socket) {
          return null;
        }

        const { icon: iconName } = mapTypeToGenericConnectorType(
          socket.standard,
        );

        const physicalSocketRealVoltage =
          socket.powerType === PowerType.AC_3_PHASE
            ? computeTreePhaseVoltage(Number.parseInt(`${socket.voltage}`, 10))
            : socket.voltage;

        const physicalSocketPower =
          socket.powerType && physicalSocketRealVoltage && socket.amperage
            ? ((socket.amperage * physicalSocketRealVoltage) / 1000).toFixed(0)
            : '?';

        return (
          <Tag
            key={socket.id}
            className={classNames(status.color, css.connectorTag)}>
            <Tooltip
              content={`${physicalSocketPower} kW | ${t(
                `delmonicos-shared-sdk.ocpp-enums-charge-point-status.${socket.ocppStatus ?? 'Unknown'}`,
              )}`}>
              <Icon name={iconName} />
            </Tooltip>
          </Tag>
        );
      })}
    </Stack>
  );
};
